import React, { useEffect, useState, useRef, useCallback } from "react";
import { Container, Nav, Navbar, NavDropdown } from "react-bootstrap";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import logo from "../../Resources/Transparent_Logo.png";
import Notifications from "../NotesTasks/Notification";
import { SupportModal } from "../Support/SupportModal";
import { purgeAll as purgeSyncToIDB } from "../SyncTo/hooks/useSyncToIDB";
import "./BootNav.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { io } from "socket.io-client";

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002";

// When Azure Web PubSub is configured, clients must connect to the PubSub
// endpoint (not the backend directly). The backend registers as the hub server
// and Azure routes messages between clients and server.
const SOCKET_URL = process.env.REACT_APP_PUBSUB_PROD_URL || process.env.REACT_APP_PUBSUB_URL || URL;
const SOCKET_PATH = (process.env.REACT_APP_PUBSUB_PROD_URL || process.env.REACT_APP_PUBSUB_URL)
  ? `/clients/socketio/hubs/${process.env.REACT_APP_PUBSUB_HUB || "Hub"}`
  : "/socket.io";

/**
 * mergeNotifications(prev, incoming)
 *
 * Merges the authoritative DB list (incoming) with the current state (prev).
 * - Incoming DB records win over any existing copy with the same _id.
 * - Synthetic entries from prev (those with no _id, e.g. statement-run-progress)
 *   are always preserved — the API never returns them.
 * - Result is sorted by timestamp descending.
 */
function mergeNotifications(prev, incoming) {
  // Synthetic entries from prev: those with no _id (e.g. statement-run progress)
  const syntheticFromPrev = prev.filter((n) => !n._id);
  // Return incoming (authoritative DB records) + synthetic-only prev entries, sorted by timestamp desc
  return [
    ...incoming.map((n) => ({ ...n, id: n._id || n.id })),
    ...syntheticFromPrev,
  ].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
}

function NavBar({
  notifications,
  addNotification,
  onRemoveNotification,
  onMarkAsRead,
  setNotifications,
}) {
  const [expanded, setExpanded] = useState(false);
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [userRole, setUserRole] = useState(false); // SuperAdmin
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const socketRef = useRef(null);
  const userIdRef = useRef(null);
  const notificationsLoadedRef = useRef(false);

  const stableAddNotification = useCallback(
    (notification) => {
      if (typeof addNotification === "function") addNotification(notification);
    },
    [addNotification]
  );

  const loadNotifications = useCallback(
    async (userId, { force = false } = {}) => {
      if (!force && notificationsLoadedRef.current && userIdRef.current === userId)
        return;
      try {
        const response = await axios.get(`${URL}/notifications`);
        const dbNotifications = response.data.map((n) => ({ ...n, id: n._id }));
        if (force) {
          setNotifications((prev) => mergeNotifications(prev, dbNotifications));
        } else {
          setNotifications(dbNotifications);
        }
        notificationsLoadedRef.current = true;
        userIdRef.current = userId;
      } catch (e) {
        console.error("Failed to load notifications:", e);
      }
    },
    [setNotifications]
  );

  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (!token) {
      notificationsLoadedRef.current = false;
      userIdRef.current = null;
      return;
    }

    let decoded;
    try {
      decoded = jwtDecode(token);
      setUserFirstName(decoded.firstName || "");
      setUserLastName(decoded.lastName || "");

      const rolesFromRole = Array.isArray(decoded?.role)
        ? decoded.role
        : decoded?.role
        ? [decoded.role]
        : [];
      const rolesFromPermissions = Array.isArray(decoded?.permissions)
        ? decoded.permissions
        : [];
      const roles = [...new Set([...rolesFromRole, ...rolesFromPermissions])];
      setUserRole(roles.some((r) => ["SuperAdmin"].includes(r)));
      setIsSystemAdmin(roles.some((r) => r === "SystemAdmin"));

      axios.defaults.headers.common["authorization"] = `Bearer ${token}`;
    } catch (e) {
      console.error("Failed to decode token:", e);
      return;
    }

    if (!decoded._id) return;

    loadNotifications(decoded._id);

    if (!socketRef.current || userIdRef.current !== decoded._id) {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
      }

      const socket = io(SOCKET_URL, {
        path: SOCKET_PATH,
        auth: { token },                    // JWT for Phase 26 handshake middleware
        query: { userId: decoded._id },     // local-dev bypass fallback (server reads query.userId when WEB_PUBSUB_CONNECTION_STRING is absent)
        transports: ["websocket", "polling"],
        forceNew: true,
      });

      socketRef.current = socket;

      // Fires on initial connect AND every successful reconnect after disconnect
      socket.on("connect", () => {
        loadNotifications(decoded._id, { force: true });
      });

      socket.on("pdf-generation-complete", (data) =>
        stableAddNotification({
          message: data.message,
          downloadId: data.downloadId,
          type: "pdf",
          timestamp: Date.now(),
          id: data.id || Date.now() + Math.random(),
        })
      );

      socket.on("appointment-confirmation-email-sent", (data) =>
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "email",
          id: data.id || Date.now() + Math.random(),
        })
      );

      socket.on("statement-email-sent", (data) => {
        console.log("Statement email notification received:", data);
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "email",
          id: data.id || `${Date.now()}-${Math.random()}`,
        });
      });

      socket.on("invoice-email-sent", (data) =>
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "email",
          id: data.id || `${Date.now()}-${Math.random()}`,
        })
      );

      socket.on("receipt-email-sent", (data) =>
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "email",
          id: data.id || `${Date.now()}-${Math.random()}`,
        })
      );

      socket.on("creditnote-email-sent", (data) =>
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "email",
          id: data.id || `${Date.now()}-${Math.random()}`,
        })
      );

      socket.on("era-received", (data) =>
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "era",
          id: data.id || `${Date.now()}-${Math.random()}`,
        })
      );

      socket.on("statement-run-progress", (data) => {
        const processed = data.processed || 0;
        const total = data.total || 0;
        // Update or add a single notification per run showing progress
        setNotifications((prev) => {
          const existingIdx = prev.findIndex(
            (n) => n.type === "statement-run" && n.meta?.runId === data.runId
          );
          const notification = {
            id: `sr-${data.runId}`,
            message: `Bulk statements: ${processed}/${total}`,
            timestamp: data.timestamp || Date.now(),
            type: "statement-run",
            isRead: false,
            meta: {
              runId: data.runId,
              processed,
              total,
              status: data.status || "RUNNING",
            },
          };
          if (existingIdx >= 0) {
            const updated = [...prev];
            updated[existingIdx] = notification;
            return updated;
          }
          return [notification, ...prev];
        });
      });

      socket.on("statement-run-complete", (data) => {
        const processed = data.processed || 0;
        const total = data.total || 0;
        const statusLabel = data.status?.replace(/_/g, " ").toLowerCase() || "completed";
        setNotifications((prev) => {
          const existingIdx = prev.findIndex(
            (n) => n.type === "statement-run" && n.meta?.runId === data.runId
          );
          const notification = {
            id: `sr-${data.runId}`,
            message: `Bulk statements ${statusLabel}: ${processed}/${total}`,
            timestamp: Date.now(),
            type: "statement-run",
            isRead: false,
            meta: {
              runId: data.runId,
              processed,
              total,
              status: data.status || "COMPLETED",
            },
          };
          if (existingIdx >= 0) {
            const updated = [...prev];
            updated[existingIdx] = notification;
            return updated;
          }
          return [notification, ...prev];
        });
      });

      socket.on("support-request-created", (data) =>
        stableAddNotification({
          message: data.message || "Support request submitted successfully",
          timestamp: data.timestamp || Date.now(),
          type: "support",
          id: data.id || `${Date.now()}-${Math.random()}`,
        })
      );
    }

    return () => {
      if (socketRef.current && userIdRef.current !== decoded._id) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [loadNotifications, stableAddNotification]);

  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        notificationsLoadedRef.current = false;
        userIdRef.current = null;
      }
    };
  }, []);

  const handleLogout = () => {
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    notificationsLoadedRef.current = false;
    userIdRef.current = null;
    // Phase 30 — PITFALLS #6 site B: purge SyncTo IndexedDB rows before the
    // session clears so recordings cannot leak across users on a shared
    // workstation. Fire-and-forget; handleLogout is synchronous and must stay
    // so (upstream callers rely on immediate state teardown).
    try { purgeSyncToIDB().catch(() => {}); } catch {}
    sessionStorage.removeItem("user");
    setExpanded(false);
  };

  const closeMenu = () => setExpanded(false);

  const isActive = (hash) => window.location.hash.startsWith(hash);

  return (
    <Navbar
      expand="lg"
      className="fixed-top bg-white brand-topbar"
      expanded={expanded}
      onToggle={(isExpanded) => setExpanded(isExpanded)}
    >
      <Container fluid className="px-lg-3">
        {/* Brand (left) */}
        <Navbar.Brand href="#/home" className="brand ms-4 order-0">
          <img className="logo" src={logo} alt="Logo" />
        </Navbar.Brand>

        {/* Right actions (desktop only) — we keep this OUTSIDE collapse and push to far right */}
        <div className="topbar-actions ms-auto d-none d-lg-flex align-items-center order-lg-2">
          <Notifications
            notifications={notifications}
            onRemoveNotification={onRemoveNotification}
            onMarkAsRead={onMarkAsRead}
          />

          <NavDropdown
            className="user-dropdown ms-2 rounded-0"
            align="end"
            id="user-nav-dropdown"
            title={
              <span className="user-trigger">
                <span className="name ms-2">
                  {userFirstName} {userLastName}
                </span>
              </span>
            }
          >
            <NavDropdown.Item href="#/userprofile" onClick={closeMenu}>
              Your Profile
            </NavDropdown.Item>
            <NavDropdown.Divider />
            <NavDropdown.Item href="#/" onClick={handleLogout}>
              Log Out
            </NavDropdown.Item>
          </NavDropdown>
        </div>

        {/* Toggler (always visible) */}
        <Navbar.Toggle
          aria-controls="basic-navbar-nav"
          className="border-0 order-1 ms-auto d-lg-none"
        />

        {/* Collapsed content (mobile) & middle rail (desktop) */}
        <Navbar.Collapse id="basic-navbar-nav" className="order-3 order-lg-1">
          {/* Primary rail (desktop) */}
          <Nav className="nav-rail mx-lg-2 d-none d-lg-flex">
            <Nav.Link
              className={`nav-pill ${isActive("#/home") ? "active" : ""}`}
              href="#/home"
              onClick={closeMenu}
            >
              Dashboard
            </Nav.Link>

            <Nav.Link
              className={`nav-pill ${isActive("#/bookings") ? "active" : ""}`}
              href="#/bookings"
              onClick={closeMenu}
            >
              Appointments
            </Nav.Link>

            <Nav.Link
              className={`nav-pill ${isActive("#/billing") ? "active" : ""}`}
              href="#/billing"
              onClick={closeMenu}
            >
              Accounts
            </Nav.Link>

            {userRole && (
              <Nav.Link
                className={`nav-pill ${isActive("#/practice") ? "active" : ""}`}
                href="#/practice"
                onClick={closeMenu}
              >
                Practice Settings
              </Nav.Link>
            )}
            {isSystemAdmin && (
              <Nav.Link
                className={`nav-pill ${isActive("#/datamigration") ? "active" : ""}`}
                href="#/datamigration"
                onClick={closeMenu}
              >
                Data Migration
              </Nav.Link>
            )}
          </Nav>

          {/* Mobile-only menu (limited items) */}
          <Nav className="d-lg-none px-2 pt-2 border-top mt-2">
            <Nav.Link href="#/home" onClick={closeMenu}>
              Dashboard
            </Nav.Link>
            <Nav.Link href="#/bookings" onClick={closeMenu}>
              Appointments
            </Nav.Link>
            <Nav.Link href="#/userprofile" onClick={closeMenu}>
              Your Profile
            </Nav.Link>
            <Nav.Link
              onClick={() => {
                closeMenu();
                setShowSupportModal(true);
              }}
              style={{ cursor: "pointer" }}
            >
              Support
            </Nav.Link>
            <Nav.Link href="#/" onClick={handleLogout}>
              Log Out
            </Nav.Link>
          </Nav>
        </Navbar.Collapse>

        {/* Mobile floating notifications (bottom-right) */}
        <div className="mobile-fab d-lg-none rounded-circle">
          <Notifications
            notifications={notifications}
            onRemoveNotification={onRemoveNotification}
            onMarkAsRead={onMarkAsRead}
          />
        </div>
      </Container>

      <SupportModal
        show={showSupportModal}
        onHide={() => setShowSupportModal(false)}
      />

      {/* Floating support FAB (desktop only) 
      <button
        className="support-fab d-none d-lg-flex"
        title="Support"
        onClick={() => setShowSupportModal(true)}
      >
        <i className="fa-light fa-life-ring text-white
        " />
      </button> */}
    </Navbar>
  );
}

export default NavBar;
